import posthog from 'posthog-js';

// Separates "network blocks the chat host" from "network blocks only the websocket upgrade"
// from "not the network" when the chat connect fails: https ok + ws fail means an upgrade
// block, both fail means the host is unreachable, both ok means look elsewhere.
export const CHAT_CONNECT_DIAGNOSTIC_EVENT = 'chat_connect_diagnostic';

const PROBE_TIMEOUT_MS = 8000;
// Mirrors the baseURL App.tsx gives the chat client; used only if the client reports none.
const DEFAULT_CHAT_BASE_URL = 'https://chat.stream-io-api.com';

export interface ProbeResult {
  ok: boolean;
  ms: number;
  closeCode?: number;
}

let reported = false;

const elapsed = (startedAt: number) =>
  Math.round(performance.now() - startedAt);

export async function probeChatHttps(baseUrl: string): Promise<ProbeResult> {
  const startedAt = performance.now();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    // no-cors resolves with an opaque response when the host is reachable and rejects
    // only on a network-level failure, which is the signal needed.
    await fetch(baseUrl, { mode: 'no-cors', signal: controller.signal });
    return { ok: true, ms: elapsed(startedAt) };
  } catch {
    return { ok: false, ms: elapsed(startedAt) };
  } finally {
    clearTimeout(timer);
  }
}

export function probeChatWebsocket(
  baseUrl: string,
  apiKey: string,
): Promise<ProbeResult> {
  const startedAt = performance.now();
  return new Promise((resolve) => {
    let opened = false;
    let settled = false;
    let socket: WebSocket | undefined;

    const settle = (result: ProbeResult) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      try {
        socket?.close();
      } catch {
        // already closed
      }
      resolve(result);
    };

    const timer = setTimeout(
      () => settle({ ok: opened, ms: elapsed(startedAt) }),
      PROBE_TIMEOUT_MS,
    );

    try {
      // Unauthenticated on purpose: the server completes the upgrade and then rejects the
      // session, so `open` firing proves the upgrade got through.
      const url = new URL('/connect', baseUrl);
      url.protocol = 'wss:';
      url.searchParams.set('api_key', apiKey);
      socket = new WebSocket(url.toString());
    } catch {
      settle({ ok: false, ms: elapsed(startedAt) });
      return;
    }
    socket.onopen = () => {
      opened = true;
      settle({ ok: true, ms: elapsed(startedAt) });
    };
    socket.onerror = () => settle({ ok: opened, ms: elapsed(startedAt) });
    socket.onclose = (event) =>
      settle({ ok: opened, ms: elapsed(startedAt), closeCode: event.code });
  });
}

const connectionInfo = () => {
  const { connection } = navigator as Navigator & {
    connection?: { effectiveType?: string };
  };
  return {
    online: navigator.onLine,
    effectiveType: connection?.effectiveType,
  };
};

const ERROR_MESSAGE_MAX_LENGTH = 200;
const JWT_PATTERN = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const LONG_OPAQUE_PATTERN = /[A-Za-z0-9_-]{40,}/g;

const rawErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error) ?? '';
  } catch {
    return '';
  }
};

// The message is the only free text recorded, so strip anything token-shaped before it is sent.
export const describeError = (error: unknown) => {
  const rawMessage = rawErrorMessage(error);
  return {
    errorName: error instanceof Error ? error.name : typeof error,
    errorMessage: rawMessage
      .replace(JWT_PATTERN, '[redacted]')
      .replace(LONG_OPAQUE_PATTERN, '[redacted]')
      .slice(0, ERROR_MESSAGE_MAX_LENGTH),
  };
};

// `token` is the token request to our API, `connect` is the chat connection itself.
export type ChatConnectStage = 'token' | 'connect';

export interface ChatConnectFailure {
  stage: ChatConnectStage;
  baseUrl: string | undefined;
  apiKey: string;
  error: unknown;
  /** performance.now() when initialization started. */
  startedAt: number;
}

/** Probes the chat host and records one event per page load. Never throws. */
export async function reportChatConnectFailure({
  stage,
  baseUrl: configuredBaseUrl,
  apiKey,
  error,
  startedAt,
}: ChatConnectFailure): Promise<void> {
  if (reported) {
    return;
  }
  reported = true;
  // Taken before the probes run so it measures the failed attempt, not the diagnostic.
  const msSinceStart = elapsed(startedAt);
  const baseUrl = configuredBaseUrl ?? DEFAULT_CHAT_BASE_URL;

  try {
    const [https, websocket] = await Promise.all([
      probeChatHttps(baseUrl),
      probeChatWebsocket(baseUrl, apiKey),
    ]);

    const payload = {
      // Only the first failure of a page load is probed; later retries are not.
      firstFailureOfPageLoad: true,
      stage,
      msSinceStart,
      isWSFailure: Boolean((error as { isWSFailure?: boolean })?.isWSFailure),
      ...describeError(error),
      https,
      websocket,
      ...connectionInfo(),
    };
    console.info(`[diag] ${CHAT_CONNECT_DIAGNOSTIC_EVENT}`, payload);
    posthog.capture(CHAT_CONNECT_DIAGNOSTIC_EVENT, payload);
  } catch {
    // diagnostics must never affect the chat flow
  }
}

export const resetChatConnectDiagnosticsForTests = () => {
  reported = false;
};
